import React, { useState, useMemo } from 'react';
import { Table, Form, InputGroup, Row, Col, Pagination, Badge } from 'react-bootstrap';

const TransactionTable = ({ data, title, hideSearch = false, hideTitle = false }) => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  // Column definitions
  const columns = [
    { key: 'id', label: 'Transaction ID' },
    { key: 'userName', label: 'User Name' },
    { key: 'type', label: 'Type' },
    { key: 'amount', label: 'Amount (₹)' },
    { key: 'transactionDate', label: 'Date' },
    { key: 'status', label: 'Status' }
  ];

  // Filter data based on search
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Global search
    if (searchQuery) {
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    return filtered;
  }, [data, searchQuery]);

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Get transaction type badge
  const getTypeBadge = (type) => {
    switch (type) {
      case 'Credit':
        return 'success';
      case 'Debit':
        return 'danger';
      default:
        return 'info';
    }
  };

  return (
    <div className="user-table-container">
      {/* Header with Title and Search */}
      {(!hideTitle || !hideSearch) && (
        <Row className="mb-3 g-2 align-items-center">
          {!hideTitle && (
            <Col lg={4} md={6} sm={12}>
              <h4 className="mb-0" style={{ color: '#1e293b', fontWeight: '700', fontSize: '1.5rem' }}>
                {title}
              </h4>
            </Col>
          )}
          {!hideSearch && (
            <Col lg={8} md={6} sm={12} className="d-flex justify-content-end">
              <InputGroup style={{ maxWidth: '300px' }}>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Col>
          )}
        </Row>
      )}

      {/* Table */}
      <div className="table-responsive" style={{ background: '#f0f2f5', borderRadius: '12px', padding: '0' }}>
        <Table bordered hover className="modern-table" style={{ background: '#f0f2f5' }}>
          <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <tr>
              {columns.map((column) => (
                <th key={column.key} style={{ padding: '12px 8px' }}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row, index) => (
                <tr key={index} style={{ background: '#f0f2f5' }}>
                  <td style={{ padding: '12px 8px' }}>{row.id}</td>
                  <td style={{ padding: '12px 8px' }}>{row.userName}</td>
                  <td style={{ padding: '12px 8px' }}>{row.type}</td>
                  <td className="fw-bold text-success" style={{ padding: '12px 8px' }}>₹{row.amount.toLocaleString()}</td>
                  <td style={{ padding: '12px 8px' }}>{row.transactionDate}</td>
                  <td style={{ padding: '12px 8px' }}>{row.status}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                  <span className="text-muted">No transactions found</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <Row className="mt-3 align-items-center">
        <Col md={6}>
          <span className="text-muted">
            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredData.length)} of {filteredData.length} entries
          </span>
        </Col>
        <Col md={6}>
          <Pagination className="justify-content-end mb-0">
            <Pagination.First 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1}
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
            />
            
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <Pagination.Ellipsis key={pageNumber} disabled />;
              }
              return null;
            })}
            
            <Pagination.Next 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
            />
            <Pagination.Last 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </Col>
      </Row>
    </div>
  );
};

export default TransactionTable;

